import { useEffect, useState } from 'react';
import { hashUrlToFileName } from './hashUrlToFileName';
import { usePreWarmCacheManager } from './PreWarmCacheContext';
import { PreWarmItem, PreWarmStatus } from './PreWarmCacheManager';

/**
 * Pre-warm the file cache with a keyed batch of files.
 *
 * Two call shapes:
 *   - `{ key, list }`  — explicit `{ fileName, url }` items (you own the keys).
 *   - `{ key, urls }`  — just URLs; each `fileName` is derived via
 *     {@link hashUrlToFileName}, the same scheme {@link useCachedUrl} reads
 *     with, so prewarmed URLs are hits there.
 *
 * Pass `undefined` to stay inactive (returns `undefined`). When active, the hook
 * subscribes to the {@link PreWarmCacheManager} for `key` and returns the live
 * {@link PreWarmStatus} (progress, counts, phase). Multiple components passing
 * the same `key` share one download set; only the first batch is used.
 *
 * The subscription tracks `key` only — changing the list/urls for the same `key`
 * does NOT restart work (the first batch is bound until the key is released,
 * i.e. all subscribers unmount).
 */
export const usePreWarmCache = (
  arg?: { key: string; list: PreWarmItem[] } | { key: string; urls: string[] },
): PreWarmStatus | undefined => {
  const manager = usePreWarmCacheManager();
  const [status, setStatus] = useState<PreWarmStatus | undefined>(undefined);

  const key = arg?.key;
  useEffect(() => {
    if (!arg) {
      setStatus(undefined);
      return;
    }
    const list = 'list' in arg ? arg.list : arg.urls.map(url => ({ fileName: hashUrlToFileName(url), url }));
    return manager.subscribe(arg.key, list, setStatus);
    // Re-subscribe only when the key (or manager) changes — the batch for the
    // same key is intentionally ignored after the first (see the doc above).
  }, [manager, key]);

  return arg ? status : undefined;
};
